import { ArrowRight, CheckCircle, CreditCard, Sparkles } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

const BillingSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      // Show success message
      toast({
        title: "🎉 Payment Successful!",
        description: "Processing your subscription... Please wait a moment.",
      });

      // Wait for webhook to process (usually takes 1-3 seconds)
      // Poll for plan update
      const checkPlanUpdate = async () => {
        try {
          // Give webhook time to process
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Redirect to settings after giving webhook time
          toast({
            title: "✅ Plan Activated!",
            description: "Your subscription has been activated. Redirecting to settings...",
          });

          // Redirect to settings after a short delay with state to trigger refresh
          setTimeout(() => {
            navigate("/settings", {
              replace: true,
              state: { fromPayment: true }
            });
          }, 1500);
        } catch (error) {
          console.error("Error checking plan update:", error);
          // Redirect anyway after delay
          setTimeout(() => {
            navigate("/settings", { replace: true });
          }, 3000);
        }
      };

      checkPlanUpdate();
    }
  }, [sessionId, navigate, toast]);

  const handleGoToSettings = () => {
    navigate("/settings");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-4 rounded-full bg-green-100 dark:bg-green-900/30 w-16 h-16 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-3xl font-bold text-green-900 dark:text-green-100">
              Payment Successful!
            </CardTitle>
            <CardDescription className="text-green-800 dark:text-green-200 text-lg">
              Your subscription has been activated successfully
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Success Message */}
            <div className="text-center space-y-4">
              <p className="text-green-800 dark:text-green-200">
                Thank you for your purchase! Your new plan is now active and
                ready to use.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                Plan Activated
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-white/50 dark:bg-gray-900/50 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-4">
                What's Next?
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-800 dark:text-green-200">
                    Check your current plan in settings
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-800 dark:text-green-200">
                    Start using identity verification services
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-800 dark:text-green-200">
                    Manage your monthly usage
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleGoToSettings}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Go to Settings
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BillingSuccessPage;
